import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
  Alert,
  TablePagination,
  Stack,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrganizationId } from '../utils/organizationContext';

const formatDate = (date) => {
  if (!date) return 'No date';
  return format(new Date(date), 'MMM d, yyyy');
};

const AssignNewConverts = () => {
  const [newConverts, setNewConverts] = useState([]);
  const [unitMembers, setUnitMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { organizationSlug: urlSlug } = useParams();
  const { user, organizationSlug } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useState(null);

  // Get service unit from user
  const selectedServiceUnit = user?.service_unit || '';

  // Load organization ID from user or URL slug
  useEffect(() => {
    const loadOrgId = async () => {
      // If user has organization_id, use it directly
      if (user?.organization_id) {
        setCurrentOrgId(user.organization_id);
        return;
      }
      
      // If user is super-admin and we have organization slug in URL, get org ID
      const slug = urlSlug || organizationSlug;
      if (slug && user?.role === 'system_admin' && !user?.organization_id) {
        try {
          const orgId = await getOrganizationId(slug);
          if (orgId) {
            setCurrentOrgId(orgId);
          }
        } catch (err) {
          console.error('Error fetching organization ID:', err);
        }
      }
    };
    loadOrgId();
  }, [urlSlug, organizationSlug, user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const params = new URLSearchParams({
          page: (page + 1).toString(),
          limit: rowsPerPage.toString()
        });
        
        if (currentOrgId && (user?.role === 'system_admin' || user?.organization_id === currentOrgId)) {
          params.append('organization_id', currentOrgId.toString());
        }

        // Fetch new converts for assignment
        const newConvertsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/new-convert-assignments/new-converts?${params}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        // Fetch unit members
        const unitMembersParams = new URLSearchParams();
        if (currentOrgId && (user?.role === 'system_admin' || user?.organization_id === currentOrgId)) {
          unitMembersParams.append('organization_id', currentOrgId.toString());
        }
        
        const unitMembersResponse = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/assignments/unit-members?${unitMembersParams}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        setNewConverts(newConvertsResponse.data.data || []);
        setTotalCount(newConvertsResponse.data.total || 0);
        setUnitMembers(unitMembersResponse.data || []);
        
        setLoading(false);
        setError('');
      } catch (err) {
        console.error('Error fetching data:', err);
        const errorMessage = err.response?.data?.error || 'Failed to load data. Please try again later.';
        setError(errorMessage);
        setNewConverts([]);
        setUnitMembers([]);
        setLoading(false);
      }
    };

    if (currentOrgId || user?.organization_id) {
      fetchData();
    }
  }, [page, rowsPerPage, user, currentOrgId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAssign = async (newConvertId, memberId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/new-convert-assignments/assign`,
        {
          new_convert_id: newConvertId,
          assigned_to: memberId
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Update the UI to reflect the assignment
      setNewConverts(prevConverts => prevConverts.map(convert => {
        if (convert.id === newConvertId) {
          const assignedMember = unitMembers.find(member => member.id === memberId);
          if (assignedMember) {
            return {
              ...convert,
              assigned_to: {
                id: assignedMember.id,
                first_name: assignedMember.first_name,
                last_name: assignedMember.last_name
              }
            };
          }
          return convert;
        }
        return convert;
      }));
      
      setSuccess('New convert assigned successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error assigning new convert:', err);
      setError('Failed to assign new convert');
    }
  };

  const isUnassigned = (convert) => {
    return !convert.assigned_to || !convert.assigned_to.first_name;
  };

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 2, my: 3 }}>
        <Typography variant="h4" gutterBottom>
          Assign New Converts
        </Typography>

        {/* Available Unit Members Table */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Available Unit Members{selectedServiceUnit ? ` in ${selectedServiceUnit.charAt(0).toUpperCase() + selectedServiceUnit.slice(1)} Unit` : 'All Units'}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>S/N</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unitMembers.map((member, index) => (
                  <TableRow key={member.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{member.first_name} {member.last_name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.phone_number || 'N/A'}</TableCell>
                    <TableCell>
                      {member.role === 'unit_manager' ? 'Unit Manager' :
                       member.role === 'system_admin' ? 'System Admin' :
                       'Unit Member'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>

            {isMobile ? (
              <Stack spacing={2}>
                {newConverts.map((convert) => (
                  <Card key={convert.id} sx={isUnassigned(convert) ? { backgroundColor: 'rgba(255, 152, 0, 0.05)' } : {}}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {convert.first_name} {convert.last_name}
                          </Typography>
                          {isUnassigned(convert) ? (
                            <Chip label="Unassigned" color="warning" size="small" />
                          ) : (
                            <Chip label="Assigned" color="success" size="small" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Date: {formatDate(convert.date)}
                        </Typography>
                        <Box>
                          <Typography variant="body2">{convert.phone_number || 'No phone'}</Typography>
                          <Typography variant="body2">{convert.email || 'No email'}</Typography>
                        </Box>
                        <Box>
                          {convert.prayer_request && (
                            <Typography variant="body2" color="text.secondary">
                              {convert.prayer_request.length > 50 
                                ? `${convert.prayer_request.substring(0, 50)}...` 
                                : convert.prayer_request}
                            </Typography>
                          )}
                          {convert.requires_counselling && (
                            <Chip 
                              label="Requires Counselling" 
                              color="info" 
                              size="small" 
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                        {!isUnassigned(convert) && (
                          <Typography variant="body2" color="text.secondary">
                            Assigned to: {convert.assigned_to.first_name} {convert.assigned_to.last_name}
                          </Typography>
                        )}
                        <FormControl fullWidth size="small">
                          <Select
                            value=""
                            onChange={(e) => handleAssign(convert.id, e.target.value)}
                          >
                            {unitMembers.map((member) => (
                              <MenuItem key={member.id} value={member.id}>
                                {member.first_name} {member.last_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const slug = urlSlug || organizationSlug;
                            navigate(slug ? `/${slug}/new-converts/${convert.id}` : `/new-converts/${convert.id}`);
                          }}
                        >
                          View Details
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Additional Info</TableCell>
                      <TableCell>Currently Assigned To</TableCell>
                      <TableCell>Assign To</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {newConverts.map((convert) => (
                      <TableRow 
                        key={convert.id}
                        sx={isUnassigned(convert) ? { backgroundColor: 'rgba(255, 152, 0, 0.05)' } : {}}
                      >
                        <TableCell>
                          {isUnassigned(convert) ? (
                            <Chip label="Unassigned" color="warning" size="small" />
                          ) : (
                            <Chip label="Assigned" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDate(convert.date)}
                        </TableCell>
                        <TableCell>
                          {convert.first_name} {convert.last_name}
                        </TableCell>
                        <TableCell>
                          <Box>
                            {convert.phone_number || 'No phone'}
                            <br />
                            {convert.email || 'No email'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {convert.prayer_request && (
                              <Typography variant="body2" color="text.secondary">
                                {convert.prayer_request.length > 50 
                                  ? `${convert.prayer_request.substring(0, 50)}...` 
                                  : convert.prayer_request}
                              </Typography>
                            )}
                            {convert.requires_counselling && (
                              <Chip 
                                label="Requires Counselling" 
                                color="info" 
                                size="small" 
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {!isUnassigned(convert) && (
                            <Typography variant="body2">
                              {convert.assigned_to.first_name} {convert.assigned_to.last_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value=""
                              onChange={(e) => handleAssign(convert.id, e.target.value)}
                            >
                              {unitMembers.map((member) => (
                                <MenuItem key={member.id} value={member.id}>
                                  {member.first_name} {member.last_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const slug = urlSlug || organizationSlug;
                              navigate(slug ? `/${slug}/new-converts/${convert.id}` : `/new-converts/${convert.id}`);
                            }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default AssignNewConverts;
